import type { Filter } from "@circle-react-shared/Notifications/NotificationContext";
import { prependNewNotificationToInboxTab } from "@circle-react-shared/Notifications/prependNewNotificationToInboxTab";
import {
  invalidateNotificationsCountQueryCache,
  invalidateNotificationsQueryCache,
} from "@circle-react-shared/Notifications/useHeaderNotificationsApi";
import type { Notification } from "@circle-react/types/Notification";
import type { QueryClient } from "react-query";

interface ResetNewNotificationCountEvent {
  event: "resetNewNotificationCount";
  new_notifications_count: number;
}

interface UpdateNewNotificationCountEvent {
  event: "updateNewNotificationCount";
  new_notifications_count: number;
}

interface NewNotificationEvent {
  data: {
    notification: Notification;
  };
  event: "newNotification";
}

interface RefreshNotificationsEvent {
  event: "refreshNotifications";
}

export type WebSocketNotificationEvent =
  | ResetNewNotificationCountEvent
  | UpdateNewNotificationCountEvent
  | NewNotificationEvent
  | RefreshNotificationsEvent;

export interface NotificationReceiveProps {
  activeFilter: Filter;
  eventData: WebSocketNotificationEvent;
  isNotificationsTabV2Enabled: boolean;
  queryClient: QueryClient;
  setNotificationCount: (count: number) => void;
}

export const onNotificationReceive = ({
  eventData,
  queryClient,
  setNotificationCount,
  isNotificationsTabV2Enabled,
  activeFilter,
}: NotificationReceiveProps) => {
  switch (eventData.event) {
    case "resetNewNotificationCount":
      setNotificationCount(eventData.new_notifications_count);
      break;
    case "updateNewNotificationCount":
      setNotificationCount(eventData.new_notifications_count);
      invalidateNotificationsQueryCache({ queryClient });
      break;
    case "newNotification": {
      const notification = eventData.data.notification;
      const validGroups = ["inbox", "mentions"];
      const isValidNotification =
        notification?.id &&
        validGroups.includes(notification?.notification_group);

      if (!isValidNotification && !isNotificationsTabV2Enabled) {
        console.warn(
          "Invalid notification data received via websocket for newNotification",
        );
        return;
      }

      prependNewNotificationToInboxTab({
        queryClient,
        notification,
        isNotificationsTabV2Enabled,
        activeFilter,
      });
      invalidateNotificationsCountQueryCache({ queryClient });
      break;
    }
    case "refreshNotifications":
      invalidateNotificationsQueryCache({ queryClient });
      break;
  }
};
